import settings from '@/constants/constants';
import type { OrderDetails } from '@/hooks/order/useGetOrderDetails';
import type { Price } from '@montugroup/circuit-shared';
import { Box, Button, Typography } from '@montugroup/design-system';
import LockClock from '@mui/icons-material/LockClock';
import Skeleton from '@mui/material/Skeleton';
import { getOrderStatusChipProps, OrderStatusChip } from '../OrderStatusChip/OrderStatusChip';
import type { OrderStage } from '../pharmacy-order-details/PharmacyOrderDetails';
import PharmacyOrderDetails from '../pharmacy-order-details/PharmacyOrderDetails';
import type { OrderHistoryIntervalDetails } from './OrderHistoryCard';
import {
  ActionText,
  DispenseIntervalStyledChip,
  OrderTrackingButtonContainer,
  SplitOrderDetailsContainer,
  SplitOrderPharmacyDetailsContainer,
  SplitOrderStatusChip,
} from './OrderHistoryCard.styles';
import { OrderHistoryDispenseIntervalAlert } from './OrderHistoryDispenseIntervalAlert';

type SplitOrderDetailsProps = {
  isLoading: boolean;
  orderDetails: OrderDetails[];
  intervalDetails?: OrderHistoryIntervalDetails | null;
  trackingLink: string | undefined;
};

const lineItemPrice = (price: Price | null, quantity: number | null) => {
  if (price === null || quantity === null) {
    return 'N/A';
  }
  return `$${(Number(price) * quantity).toFixed(2)}`;
};

function SplitOrderDetailsLoading() {
  return (
    <Box
      data-testid="skeleton"
      sx={(theme) => ({
        flexDirection: 'column',
        border: '1px',
        borderStyle: 'solid',
        borderColor: '#DEDEE1',
        padding: theme.spacing(5),
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(4),
      })}
    >
      <Skeleton variant="text" width="30%" height={32} />
      <Skeleton variant="text" width="40%" height={32} />
      <Skeleton variant="text" width="30%" height={32} />

      <Skeleton variant="rectangular" width="100%" height={200} sx={(theme) => ({ marginTop: theme.spacing(4) })} />
    </Box>
  );
}

function SplitOrderDetails(props: SplitOrderDetailsProps) {
  const { orderDetails, isLoading, intervalDetails } = props;

  return (
    <>
      {isLoading && (
        <Box>
          <SplitOrderDetailsLoading />
          <SplitOrderDetailsLoading />
        </Box>
      )}

      {!isLoading &&
        orderDetails?.map((order, index) => {
          const orderStatusChipProps = getOrderStatusChipProps({
            status: order.order_status_id,
            delivered: order.delivered,
          });

          const isIntervalProduct = order.OrderProducts?.some((product) => {
            if (product.product_id && order.order_status_id !== settings.orderStatus.CANCELLED) {
              return intervalDetails?.productIdsInInterval?.includes(product.product_id);
            }
            return false;
          });

          const orderNumber = order.order_code;

          return (
            <SplitOrderPharmacyDetailsContainer key={order.order_code}>
              <SplitOrderDetailsContainer>
                <Typography variant="subtitle2" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
                  Package {index + 1} #{order.order_code}
                </Typography>
                {!isIntervalProduct && (
                  <SplitOrderStatusChip>
                    <OrderStatusChip {...orderStatusChipProps} />
                  </SplitOrderStatusChip>
                )}
              </SplitOrderDetailsContainer>
              {isIntervalProduct && (
                <Box sx={{ mt: 2 }}>
                  <OrderHistoryDispenseIntervalAlert intervalDetails={intervalDetails} orderNumber={orderNumber} />
                </Box>
              )}
              <Box
                sx={(theme) => ({
                  flexDirection: 'column',
                  marginTop: theme.spacing(2),
                })}
              >
                {order.OrderProducts?.map((product) => (
                  <Box
                    key={product.id}
                    sx={(theme) => ({
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: theme.spacing(1),
                      ...(order.OrderProducts.length >= 1 &&
                        !isIntervalProduct && {
                          borderBottom: '1px',
                          borderColor: theme.palette.divider,
                          borderBottomStyle: 'solid',
                        }),
                      paddingBottom: theme.spacing(2),
                    })}
                  >
                    {isIntervalProduct &&
                      product.product_id &&
                      intervalDetails?.productIdsInInterval?.includes(product.product_id) && (
                        <Box sx={{ marginBottom: (theme) => theme.spacing(2) }}>
                          <DispenseIntervalStyledChip label="Dispense interval" icon={<LockClock color="info" />} />
                        </Box>
                      )}
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing(1),
                      })}
                    >
                      <Typography variant="body2">{product.Product.name}</Typography>
                      <Typography variant="body2" fontWeight="600">
                        {lineItemPrice(product.Product.price, product.quantity)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">Units: {product.quantity}</Typography>
                  </Box>
                ))}
              </Box>

              {!isIntervalProduct && (
                <PharmacyOrderDetails
                  fullOrderCode={order.order_code}
                  stage={orderStatusChipProps.status as OrderStage}
                  pharmacy={order.Pharmacy}
                  deliveryOption={order.DeliveryOption}
                />
              )}

              {props.trackingLink && (
                <OrderTrackingButtonContainer>
                  <Button href={props.trackingLink} fullWidth variant="outlined">
                    <ActionText variant="body1">Track delivery</ActionText>
                  </Button>
                </OrderTrackingButtonContainer>
              )}
            </SplitOrderPharmacyDetailsContainer>
          );
        })}
    </>
  );
}

export default SplitOrderDetails;
